/** Inline editor for ONE tenant's value of one concrete config path.
 *
 * Commits straight into the change-set store (`SET_EDIT`), never into local
 * page state: instant-commit widgets (Switch/Select/chip array) reuse
 * `ValueCell`, while free-text/number inputs keep a local draft and commit on
 * blur AND Enter so undo history records whole edits, not keystrokes.
 * Committing a value deep-equal to the baseline clears the entry (reducer
 * behaviour). Secrets never round-trip: the field starts empty and committing
 * empty (or the mask sentinel) removes the pending edit instead of recording
 * the sentinel.
 */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { MASKED_SECRET } from '@/api/tourOperatorConfigs';

import { safePreview } from '../config/schema-grid/schemaOps';
import type { SchemaProp } from '../config/schema-grid/types';
import { ValueCell } from '../config/schema-grid/ValueCell';

import { UNSET } from './changeSet';
import { useChangeSet, useTenantEdit } from './ChangeSetContext';

export interface DraftValueEditorProps {
  tenantId: number;
  concretePath: string;
  schema: SchemaProp | null;
  secret: boolean;
  /** Baseline value at load; `undefined` = the key is absent for this tenant. */
  original: unknown;
  /** The tenant's `config_last_modified_ts` at load. */
  baseTs: string | null;
}

/** Editor kind for the current path — schema-driven when a schema exists,
 *  inferred from the live value for observed-only (schema-less) rows. */
function editorKind(
  schema: SchemaProp | null,
  value: unknown,
): 'cell' | 'integer' | 'number' | 'string' | 'preview' {
  if (schema) {
    if (schema.type === 'boolean' || schema.enum || schema.type === 'array' || schema.items) {
      return 'cell'; // instant-commit ValueCell widgets
    }
    if (schema.type === 'integer') return 'integer';
    if (schema.type === 'number') return 'number';
    if (schema.type === 'string') return 'string';
    return 'preview'; // object / untyped
  }
  if (typeof value === 'boolean') return 'cell';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') return 'string';
  return 'preview';
}

export function DraftValueEditor({
  tenantId,
  concretePath,
  schema,
  secret,
  original,
  baseTs,
}: DraftValueEditorProps) {
  const { dispatch } = useChangeSet();
  const edit = useTenantEdit(tenantId, concretePath);
  // Free-text draft; null = mirror the committed value.
  const [draft, setDraft] = useState<string | null>(null);

  const committed = edit && edit.after !== UNSET ? edit.after : original;
  const modified = edit !== undefined;
  // Unsetting a list ELEMENT is rejected by the backend — dict keys only.
  // Pending-added entries (no baseline) get the revert button instead.
  const canUnset = !/\[\d+\]$/.test(concretePath) && original !== undefined;

  const commit = (after: unknown) =>
    dispatch({ type: 'SET_EDIT', tenantId, path: concretePath, before: original, after, baseTs });

  const commitSecret = (raw: string) => {
    if (raw === '' || raw === MASKED_SECRET) {
      dispatch({ type: 'REMOVE_EDIT', tenantId, path: concretePath });
    } else {
      commit(raw);
    }
    setDraft(null);
  };

  const commitText = (kind: 'integer' | 'number' | 'string') => {
    if (draft === null) return;
    if (kind === 'string') {
      commit(draft);
    } else {
      const parsed = kind === 'integer' ? parseInt(draft, 10) : parseFloat(draft);
      if (!Number.isNaN(parsed)) commit(parsed);
    }
    setDraft(null);
  };

  const kind = secret ? 'secret' : editorKind(schema, committed);

  let editor: React.ReactNode;
  if (kind === 'secret') {
    editor = (
      <TextField
        type="password"
        size="small"
        value={draft ?? (edit && edit.after !== UNSET ? String(edit.after) : '')}
        placeholder={`${MASKED_SECRET} (unchanged)`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== null && commitSecret(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && draft !== null) commitSecret(draft);
        }}
        sx={{ width: '100%', maxWidth: 360, '& input': { py: 0.5, fontSize: 13 } }}
      />
    );
  } else if (kind === 'cell') {
    editor = <ValueCell prop={schema} value={committed} onChange={commit} />;
  } else if (kind === 'preview') {
    editor = (
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {safePreview(committed)}
      </Typography>
    );
  } else {
    const isNumeric = kind !== 'string';
    editor = (
      <TextField
        size="small"
        {...(isNumeric ? { type: 'number', inputProps: { step: kind === 'integer' ? 1 : 0.01 } } : {})}
        value={draft ?? String(committed ?? '')}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commitText(kind)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitText(kind);
        }}
        sx={{ width: '100%', maxWidth: isNumeric ? 200 : 480, '& input': { py: 0.5, fontSize: 13 } }}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{editor}</Box>
      {modified && (
        <>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              flexShrink: 0,
            }}
          />
          <Tooltip title="Revert this change">
            <IconButton
              size="small"
              onClick={() => dispatch({ type: 'REMOVE_EDIT', tenantId, path: concretePath })}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
      {canUnset && (
        <Tooltip title="Unset for this tenant">
          <IconButton size="small" onClick={() => commit(UNSET)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
